import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Master admin: editovatelné sady přezdívek pro 3 věkové stupně.
 * Generátor čte z této collection (s fallbackem na hardcoded seed).
 */
export default function AdminNicknames() {
  const [pools, setPools] = useState([]);
  const [editing, setEditing] = useState({}); // {tier: {prefixesText, coresText}}
  const [savingTier, setSavingTier] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/nickname-pools");
      setPools(data);
      const initial = {};
      data.forEach((p) => {
        initial[p.tier] = {
          prefixesText: p.prefixes.join(", "),
          coresText: p.cores.join(", "),
        };
      });
      setEditing(initial);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error("Pouze pro master admina");
        navigate("/teacher");
      }
    }
  };

  useEffect(() => {
    load();
  }, []);

  const splitList = (s) =>
    s
      .split(/[,\n]/)
      .map((x) => x.trim())
      .filter((x) => x.length > 0);

  const save = async (tier) => {
    const e = editing[tier];
    const prefixes = splitList(e.prefixesText || "");
    const cores = splitList(e.coresText || "");
    if (!prefixes.length || !cores.length) {
      return toast.error("Předpony i jádra musí mít aspoň jednu položku");
    }
    setSavingTier(tier);
    try {
      await api.put(`/admin/nickname-pools/${tier}`, { tier, prefixes, cores });
      toast.success("Sada uložena");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nepodařilo se uložit");
    } finally {
      setSavingTier(null);
    }
  };

  const reset = async (tier) => {
    if (!confirm("Opravdu chceš obnovit výchozí sadu? Vlastní změny zmizí.")) return;
    try {
      await api.post(`/admin/nickname-pools/${tier}/reset`);
      toast.success("Obnoveno na výchozí");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset selhal");
    }
  };

  const samples = (tier) => {
    const e = editing[tier];
    if (!e) return [];
    const pref = splitList(e.prefixesText);
    const core = splitList(e.coresText);
    if (!pref.length || !core.length) return [];
    return Array.from({ length: 6 }).map(() => {
      const p = pref[Math.floor(Math.random() * pref.length)];
      const c = core[Math.floor(Math.random() * core.length)];
      const n = Math.floor(Math.random() * 90 + 10);
      return `${p}${c}${n}`;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center font-black">D</div>
            <div>
              <div className="font-heading font-black text-lg tracking-tight text-slate-900">Sady přezdívek</div>
              <div className="text-xs text-slate-500">Master admin · per stupeň školy</div>
            </div>
          </div>
          <Link to="/teacher" className="text-sm font-bold text-slate-500 hover:text-slate-900">
            ← Zpět na panel
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900">
          <strong>Jak to funguje:</strong> Generátor přezdívek skládá tvar
          <code className="font-mono bg-white px-1 mx-1">Předpona+Jádro+##</code>.
          Příklad: pro 6.–9. ZŠ <code className="font-mono bg-white px-1">Skibidi</code> + <code className="font-mono bg-white px-1">Chad</code> + <code className="font-mono bg-white px-1">42</code> = <strong>SkibidiChad42</strong>.
          Změny platí pro všechny nové připojené žáky.
        </div>

        {pools.map((p) => {
          const e = editing[p.tier] || { prefixesText: "", coresText: "" };
          return (
            <section
              key={p.tier}
              className="bg-white border border-slate-200 rounded-lg p-6 space-y-4"
              data-testid={`pool-${p.tier}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading font-black text-xl tracking-tight text-slate-900">{p.label}</h2>
                  <div className="text-xs text-slate-500 mt-0.5">tier ID: <code className="font-mono">{p.tier}</code></div>
                </div>
                <button
                  onClick={() => reset(p.tier)}
                  className="text-xs font-bold text-slate-500 hover:text-red-600"
                  data-testid={`btn-reset-${p.tier}`}
                >
                  ↺ Obnovit výchozí
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Předpony ({splitList(e.prefixesText).length})
                  </label>
                  <textarea
                    value={e.prefixesText}
                    onChange={(ev) => setEditing({ ...editing, [p.tier]: { ...e, prefixesText: ev.target.value } })}
                    rows={6}
                    className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono"
                    placeholder="Oddělené čárkou nebo novým řádkem"
                    data-testid={`input-prefixes-${p.tier}`}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Jádra ({splitList(e.coresText).length})
                  </label>
                  <textarea
                    value={e.coresText}
                    onChange={(ev) => setEditing({ ...editing, [p.tier]: { ...e, coresText: ev.target.value } })}
                    rows={6}
                    className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono"
                    placeholder="Oddělené čárkou nebo novým řádkem"
                    data-testid={`input-cores-${p.tier}`}
                  />
                </div>
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Náhled</div>
                <div className="flex flex-wrap gap-2">
                  {samples(p.tier).map((s, i) => (
                    <span key={i} className="inline-block bg-slate-100 text-slate-800 font-mono text-sm rounded-full px-3 py-1">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => save(p.tier)}
                  disabled={savingTier === p.tier}
                  className="bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-bold rounded-md px-4 py-2 text-sm"
                  data-testid={`btn-save-${p.tier}`}
                >
                  {savingTier === p.tier ? "Ukládám…" : "Uložit"}
                </button>
              </div>
            </section>
          );
        })}
      </main>
    </div>
  );
}
